import { useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useNavigate } from 'react-router-dom'
import { Download, ExternalLink, Presentation } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { env } from '@/lib/env'
import { getSignedUrl } from '@/features/output/output-api'
import { parseFrontmatter, fmString, fmNumber } from './frontmatter'

function preprocessWikilinks(content: string): string {
  return content.replace(
    /\[\[([^\]|]+?)(?:\|([^\]]+))?\]\]/g,
    (_match, target: string, display?: string) =>
      `[${display ?? target}](#wikilink:${encodeURIComponent(target.trim())})`,
  )
}

interface VaultPreviewProps {
  content: string
  onNavigate: (wikilinkTarget: string) => void
  className?: string
  /** Aktueller Vault-Pfad — fuer Signed-URL-Download bei Binary-Files. */
  filePath?: string
  /** Content-Type des Files. */
  contentType?: string
  /** B2.0-R3: first-class storage_path-Spalte. */
  storagePath?: string | null
  /** B2.0-R3: first-class image-metadata-Spalte. */
  imageMetadata?: Record<string, unknown>
}

export function VaultPreview({
  content,
  onNavigate,
  className,
  filePath,
  contentType,
  storagePath,
  imageMetadata,
}: VaultPreviewProps) {
  const navigate = useNavigate()
  const { data: fm, body } = useMemo(() => parseFrontmatter(content), [content])
  const processed = useMemo(() => preprocessWikilinks(body), [body])
  const canGeneratePresentation = isResearchMarkdown(filePath, contentType)

  // B2.0-R3: Image-Detection ueber dedizierte Spalten + content_type.
  // Fallback-Logik (Backfill, Frontmatter etc.) ist Backend-Aufgabe.
  const isImage = (contentType?.startsWith('image/') ?? false) && !!storagePath

  if (isImage && filePath) {
    return (
      <div className={cn('h-full overflow-auto', className)}>
        <div className="mx-auto max-w-[720px] px-4 min-[761px]:px-6 py-4">
          <ImageView filePath={filePath} metadata={imageMetadata ?? {}} />
        </div>
      </div>
    )
  }

  const isHtmlOutput = isHtmlFile(filePath, contentType)
  if (isHtmlOutput && filePath) {
    return (
      <div className={cn('h-full overflow-auto', className)}>
        <div className="mx-auto max-w-[720px] px-4 min-[761px]:px-6 py-4">
          {Object.keys(fm).length > 0 && <MetadataStrip data={fm} />}
          <HtmlOutputTile filePath={filePath} title={fmString(fm, 'title') ?? filePath} />
        </div>
      </div>
    )
  }

  // Andere Binary-Files (z.B. .docx, .pptx) — BinaryHeader bietet Download
  const isBinary = contentType !== undefined && !contentType.startsWith('text/')

  return (
    <div className={cn('h-full overflow-auto', className)}>
      <div className="mx-auto max-w-[720px] px-4 min-[761px]:px-6 py-4">
        {canGeneratePresentation && filePath && (
          <div className="mb-4 flex justify-end border-b border-border pb-3">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                const params = new URLSearchParams()
                params.set('prefill', `/presentation [[${filePath}]]`)
                navigate(`/chat?${params.toString()}`)
              }}
            >
              <Presentation className="mr-1.5 h-3.5 w-3.5" />
              Präsentation generieren
            </Button>
          </div>
        )}

        {Object.keys(fm).length > 0 && <MetadataStrip data={fm} />}

        {isBinary && filePath && (
          <BinaryHeader filePath={filePath} contentType={contentType ?? ''} />
        )}

        <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:font-[650] prose-headings:text-foreground prose-h1:text-[26px] prose-h1:leading-tight prose-h2:text-[16px] prose-h2:leading-tight prose-p:text-[14px] prose-p:leading-[1.45] prose-p:text-foreground prose-li:text-[14px] prose-li:leading-[1.45] prose-code:font-mono prose-code:text-[13px] prose-pre:bg-muted prose-pre:font-mono prose-pre:text-[13px] prose-blockquote:border-l-accent prose-strong:text-foreground">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a({ href, children, node: _node, ...props }) {
                if (href?.startsWith('#wikilink:')) {
                  const target = decodeURIComponent(href.slice('#wikilink:'.length))
                  return (
                    <button
                      type="button"
                      onClick={() => onNavigate(target)}
                      className="break-words font-mono text-[13px] text-primary underline underline-offset-2 hover:decoration-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
                    >
                      {children}
                    </button>
                  )
                }
                if (!href) return <>{children}</>
                const resolvedHref = resolveBackendHref(href)
                return (
                  <a
                    href={resolvedHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
                    {...props}
                  >
                    {children}
                    <ExternalLink className="h-3 w-3 opacity-60" />
                  </a>
                )
              },
              blockquote({ children, node: _node, ...props }) {
                const text = String(children)
                const isContradiction = text.includes('CONTRADICTION')
                const isUnsourced = text.includes('UNSOURCED')
                return (
                  <blockquote
                    className={cn(
                      'border-l-4 pl-4 italic',
                      isContradiction && 'border-l-yellow-400',
                      isUnsourced && 'border-l-destructive',
                      !isContradiction && !isUnsourced && 'border-l-accent',
                    )}
                    {...props}
                  >
                    {children}
                  </blockquote>
                )
              },
            }}
          >
            {processed}
          </ReactMarkdown>

          {!body.trim() && (
            <p className="text-sm text-muted-foreground italic">Noch kein Inhalt.</p>
          )}
        </div>
      </div>
    </div>
  )
}

function isResearchMarkdown(filePath?: string, contentType?: string): boolean {
  if (!filePath?.startsWith('_Research/')) return false
  if (!filePath.toLowerCase().endsWith('.md')) return false
  return !contentType || contentType.startsWith('text/') || contentType.includes('markdown')
}

function isHtmlFile(filePath?: string, contentType?: string): boolean {
  return Boolean(filePath?.toLowerCase().endsWith('.html') || contentType?.toLowerCase().startsWith('text/html'))
}

function resolveBackendHref(href: string): string {
  if (href.startsWith('/api/')) {
    return `${env.VITE_API_URL.replace(/\/+$/, '')}${href}`
  }
  return href
}

function HtmlOutputTile({ filePath, title }: { filePath: string; title: string }) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setUrl(null)
    setError(null)
    getSignedUrl(filePath)
      .then((u) => {
        if (!cancelled) setUrl(u)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Signed URL fehlgeschlagen')
      })
    return () => {
      cancelled = true
    }
  }, [filePath])

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold">HTML-Output</p>
          <p className="mt-1 truncate font-mono text-xs text-muted-foreground">{title}</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Diese Datei wird als gerendertes HTML im neuen Tab geöffnet.
          </p>
        </div>
        {url && (
          <Button asChild size="sm" variant="outline" className="shrink-0">
            <a href={url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              HTML öffnen
            </a>
          </Button>
        )}
      </div>
      {error && <p className="mt-3 text-xs text-destructive">{error}</p>}
    </div>
  )
}

// ---------- Metadata-Strip ----------

interface MetadataStripProps {
  data: Record<string, unknown>
}

function MetadataStrip({ data }: MetadataStripProps) {
  const type = fmString(data, 'type')
  const sourceType = fmString(data, 'source_type')
  const tier = fmNumber(data, 'tier')
  const lang = fmString(data, 'language')
  const date = fmString(data, 'date')
  const author = fmString(data, 'author')
  const title = fmString(data, 'title')

  const hasAnything = type ?? sourceType ?? tier ?? lang ?? date ?? author ?? title
  if (!hasAnything) return null

  return (
    <div className="mb-5 flex flex-wrap items-center gap-1.5 border-b border-border pb-3 text-[12px]">
      {(type || sourceType) && (
        <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 font-mono text-muted-foreground">
          {sourceType ? `source · ${sourceType}` : type}
        </span>
      )}
      {tier != null && <TierBadge tier={tier} />}
      {lang && (
        <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 font-mono uppercase text-muted-foreground">
          {lang}
        </span>
      )}
      {date && <span className="text-muted-foreground">{date}</span>}
      {author && <span className="text-muted-foreground">· {author}</span>}
    </div>
  )
}

function BinaryHeader({
  filePath,
  contentType,
}: {
  filePath: string
  contentType: string
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setUrl(null)
    setError(null)
    getSignedUrl(filePath)
      .then((u) => {
        if (!cancelled) setUrl(u)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Signed URL fehlgeschlagen')
      })
    return () => {
      cancelled = true
    }
  }, [filePath])

  return (
    <div className="mb-5 rounded-lg border border-border bg-muted/40 p-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="font-mono text-[12px] text-muted-foreground">{contentType}</span>
          <span className="text-sm font-medium">Binary-Datei</span>
        </div>
        {url && (
          <Button asChild size="sm" variant="outline">
            <a href={url} target="_blank" rel="noopener noreferrer">
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Herunterladen
            </a>
          </Button>
        )}
      </div>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  )
}

// ---------- B2.0-R3 Image-View ----------

function ImageView({
  filePath,
  metadata,
}: {
  filePath: string
  metadata: Record<string, unknown>
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setUrl(null)
    setError(null)
    getSignedUrl(filePath)
      .then((u) => {
        if (!cancelled) setUrl(u)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Signed URL fehlgeschlagen')
      })
    return () => {
      cancelled = true
    }
  }, [filePath])

  const prompt = String(metadata['prompt'] ?? '')
  const finalPrompt = String(metadata['final_prompt'] ?? '')
  const model = String(metadata['model'] ?? '')
  const brand = String(metadata['brand'] ?? '')
  const style = String(metadata['style'] ?? '')
  const generated = String(metadata['generated_at'] ?? '')

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-border bg-muted/30">
        {url ? (
          <img
            src={url}
            alt={prompt || filePath}
            className="block max-h-[640px] w-full object-contain bg-black/5"
          />
        ) : error ? (
          <div className="p-6 text-sm text-destructive">
            Bild nicht geladen — {error}
            <div className="mt-1 font-mono text-[11px] text-muted-foreground">{filePath}</div>
          </div>
        ) : (
          <div className="p-6 text-sm text-muted-foreground">Lade Bild …</div>
        )}
        {url && (
          <div className="flex items-center justify-between border-t border-border bg-background/60 px-3 py-2">
            <span className="truncate font-mono text-[11px] text-muted-foreground">{filePath}</span>
            <Button asChild size="sm" variant="ghost" className="h-7">
              <a href={url} target="_blank" rel="noopener noreferrer">
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Original
              </a>
            </Button>
          </div>
        )}
      </div>
      {(prompt || model || brand || style || generated || finalPrompt) && (
        <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          {prompt && <Caption label="Prompt" value={prompt} wide />}
          {finalPrompt && finalPrompt !== prompt && (
            <Caption label="Final Prompt" value={finalPrompt} wide />
          )}
          {style && <Caption label="Style" value={style} />}
          {model && <Caption label="Model" value={model} mono />}
          {brand && <Caption label="Brand" value={brand} />}
          {generated && <Caption label="Generated at" value={generated} mono />}
        </div>
      )}
    </div>
  )
}

function Caption({
  label,
  value,
  mono,
  wide,
}: {
  label: string
  value: string
  mono?: boolean
  wide?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded border border-border bg-muted/30 px-3 py-2',
        wide && 'sm:col-span-2',
      )}
    >
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('mt-0.5 break-words', mono && 'font-mono text-[13px]')}>{value}</div>
    </div>
  )
}

function TierBadge({ tier }: { tier: number }) {
  const colors: Record<number, string> = {
    1: 'bg-tier-1/10 text-tier-1 border-tier-1/30',
    2: 'bg-tier-2/10 text-tier-2 border-tier-2/30',
    3: 'bg-tier-3/10 text-tier-3 border-tier-3/30',
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded border px-1.5 py-0.5 font-mono',
        colors[tier] ?? 'bg-muted text-muted-foreground',
      )}
    >
      Tier {tier}
    </span>
  )
}
